import { Editor } from '@monaco-editor/react';
import { FilterType } from 'bindings/filters/FilterType';
import { GenericReport } from 'bindings/reports/generic/GenericReport';
import { GroupBy } from 'bindings/reports/generic/GroupBy';
import { Period } from 'bindings/reports/generic/TimeRange';
import { Variation } from 'bindings/reports/generic/Variation';
import { Granularity } from 'bindings/time/Granularity';
import close from 'images/icons/orders/close.svg';
import _ from 'lodash';
import * as React from 'react';
import { Translate } from 'react-localize-redux';
import { useHistory } from 'react-router';
import { useRecoilValue } from 'recoil';
import moment from 'moment';
import styled from 'styled-components';
import { AAdditionalColumns, additionalColumnTypeToFilterTypeAndPath, getFirstValueNotPieChart } from '../../../atoms/additionalColumns';
import { AAdditionalFieldColumns, fieldTypeToFilterType } from '../../../atoms/additionalFieldColumns';
import { AFormFields } from '../../../atoms/forms';
import { AUsers } from '../../../atoms/global/users';
import { AProducts } from '../../../atoms/product';
import Dropdown from '../../../components_v2/dropdown/Dropdown';
import { DropdownData } from '../../../components_v2/dropdown/model/Model';
import AdvancedFilters from '../../../components_v2/filter/AdvancedFilters';
import { FilterResult, FilterTree, filterTreeLength } from '../../../components_v2/filter/model/Model';
import { FilterParameter } from '../../../components_v2/filter/pages/FilterList';
import { CrossClose } from '../../../components_v2/popup/style/PopupCreationStyle';
import Switch from '../../../components_v2/Switch/Switch';
import { useWindowDimensions } from '../../../components_v2/utils';
import { ComponentProps } from '../../../containers_v2/globals/mainPage/mainPage';
import { getLocalStorage } from '../../../helpers/localStorage';
import RefreshButton from '../../../myToolbar/RefreshButton';
import { DefaultButton } from '../../../styles/global/css/GlobalButton';
import { PrimaryColor, GreySidely, LightGreySidely2, SidelyBlack, SurfaceColor } from '../../../styles/global/css/Utils';
import { translateToString } from '../../../styles/global/translate';
import { useFunctionState } from '../../../utils/customHooks';
import { AlertRes } from '../../alert/AlertProvider';
import useAlert from '../../alert/UseAlert';
import { getCompanyFilters } from '../../client-companies/data/action';
import { generatePromotionFilters } from '../../../components_v2/filter/promotionFilters';
import { EditorWrapper, OptionsModal, TitleAndChild } from '../../client-companies/popup/Detail';
import { LoadingStateEnum } from '../../import/model';
import { ComponentLoader } from '../../map/modalRight/ModalCalendar';
import NoData from '../../noData/NoData';
import PermissionContext from '../../permissions/PermissionContext';
import { FlexDiv } from '../../products/style';
import { getReportById, putReport } from '../action';
import { Report } from '../interpretor/bareReportingDecoder';
import ReportInterpretor from '../interpretor/ReportInterpretor';
import { getGenericAndDecodeReport, getGoals, Goal } from './action';
import { reportErrorMessage } from './errorMessages';
import { getFinalyFilteredProducts, LocalFilters, ReportFiltersContext, ReportFiltersProvider } from './generic';
import { fetchProductFieldValues, isFilterableProductField, ProductFieldFilter, productFieldFilterIds } from './productFieldFilter';
import { isExternalColumn, RecursiveReportEditor } from './genericEditorRecursive';
import { LocalStorageReport } from './localReport';
import { TOOLBARS_HEIGHT } from '../../globals/defaultToolbar/style/Style';
import { DefaultInput } from '../../../styles/global/css/GlobalInput';

export const AllPeriods: string[] = ['Today', 'ThisWeek', '7DaysAgo', 'LastWeek', 'ThisMonth', '30DaysAgo', 'LastMonth', '90DaysAgo', 'ThisYear', '365DaysAgo', 'LastYear'];

// A range bound that is not a named preset is a literal date — the backend
// parses "%Y-%m-%dT%H:%M:%S" before falling back to the presets.
export const CHOOSE_DATE = 'ChooseDate';
export function isCustomRangeDate(value: string | undefined): boolean {
	return !!value && !AllPeriods.includes(value);
}

export const BeginPeriods: Period[] = ['Today', 'ThisWeek', '7DaysAgo', 'LastWeek', 'ThisMonth', '30DaysAgo', 'LastMonth', 'ThisYear', '365DaysAgo', 'LastYear'];
export const EndPeriods: Period[] = ['ThisWeek', '7DaysAgo', 'LastWeek', 'ThisMonth', '30DaysAgo', 'LastMonth', '90DaysAgo', 'ThisYear', '365DaysAgo', 'LastYear'];


const Tab = styled.div<{ selected?: boolean }>`
	padding: 5px 15px;
	border: 1px solid ${GreySidely};
	border-top-left-radius: 5px;
	border-top-right-radius: 5px;
	background-color: ${p => p.selected ? PrimaryColor : LightGreySidely2};
	cursor: pointer;
	color: ${p => p.selected ? LightGreySidely2 : SidelyBlack};
	user-select: none;
`;

// Native date input shown when a range bound is a literal date.
const RangeDateInput = styled.input`
	${DefaultInput}
	width: 200px;
`;

const ReportOption = styled(FlexDiv)`
	width: 100%;
	justify-content: space-between;
`;

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	height: 100%;
	width: 416px;
	flex-shrink: 0;
	background-color: ${SurfaceColor};
	padding: 10px 10px 20px 10px;
	border-radius: 10px;
`;

const InnerWrapper = styled.div`
	height: 100%;
	display: flex;
	flex-direction: column;
	gap: 12px;
	padding: 10px;
	border-radius: 10px;
	overflow-y: auto;
	overflow-x: hidden;
`;

enum Views {
	Code,
	Visual
}

export const GenericEditorContext = React.createContext<{ report: GenericReport, setReport:(value: GenericReport) => void }>({ report: {} as GenericReport, setReport: () => undefined });

export function GenericEditorProvider(props: { report: string, setReport: (value: string) => void, children: React.ReactNode }): JSX.Element {
	const [report, setReport] = useFunctionState<GenericReport>(JSON.parse(props.report), ({ newValue }) => {
		props.setReport(JSON.stringify(newValue, null, 2));
		return newValue;
	});

	return <GenericEditorContext.Provider value={{ report, setReport }}>
		{props.children}
	</GenericEditorContext.Provider>;
}

// before
/*function getLastFromFirstPhysicalColumnGroup(report: GenericReport): number {
	const physicalColumns = ['AdditionalColumn', 'CompanyColumn', 'ProductColumn', 'InstanceColumn'];

	let lastPhysicalColumn = 0;
	report.panels[0].columns.forEach((column, index) => {

		if (typeof column !== 'string' && physicalColumns.find((physicalColumn) => physicalColumn in column)) {
			lastPhysicalColumn = index + 1;
		} else {
			return lastPhysicalColumn - 1;
		}
	});
	return lastPhysicalColumn;
} */

function getLastFromFirstPhysicalColumnGroup(report: GenericReport): number {
	const physicalColumns = ['AdditionalColumn', 'CompanyColumn', 'ProductColumn', 'InstanceColumn'];

	let lastPhysicalColumn = 0;
	report.panels[0].columns.forEach((column, index) => {
		if (typeof column !== 'string' && physicalColumns.find((physicalColumn) => physicalColumn in column)) {
			lastPhysicalColumn = index + 1;
		} else {
			return lastPhysicalColumn - 1;
		}
	});
	return lastPhysicalColumn;
}

function setVariation(report: GenericReport, value: Variation | undefined): GenericReport {
	report.variation = value;
	if (report.explode_by && report.new_group_by) {
		report.new_group_by -= 1;
	}
	if (report.variation !== undefined) {
		report.explode_by = undefined;
		report.new_group_by = getLastFromFirstPhysicalColumnGroup(report);
	}
	return { ...report };
}

function setEvolutionBy(report: GenericReport, value: Granularity | undefined): GenericReport {
	if (!value) {
		report.evolution = undefined;
	} else {
		report.evolution = value;
	}
	return { ...report };
}

const ReportTypes: DropdownData[] = [
	{ value: 'evolution', label: 'report_editor.evolution' },
	{ value: 'analyze', label: 'report_editor.analyze' },
];

export const DataSources: DropdownData[] = [
	{ value: 'Company', label: 'company' },
	{ value: 'User', label: 'user' },
	{ value: 'Instance', label: 'instance' },
];

function VisualEditor(props: {
	report_id?: number
	report: string | undefined
	setReport: (value: string) => void
	refresh: boolean
	setRefresh: (value: boolean) => void
}): JSX.Element {
	const report = props.report || JSON.stringify({});

	React.useEffect(() => {
		if (!props.report) {
			props.setReport(JSON.stringify({}));
		}
	}, [props.report]);

	return <GenericEditorProvider report={report} setReport={props.setReport}>
		<ReportFiltersProvider genericReport={JSON.parse(report)}>
			<_VisualEditor report_id={props.report_id} refresh={props.refresh} setRefresh={props.setRefresh} />
		</ReportFiltersProvider>
	</GenericEditorProvider>;
}

const ReportWrapper = styled.div`
	background-color: ${SurfaceColor};
	padding: 10px;
	border-radius: 10px;
	overflow: auto;
	flex: 1 1 0;
	min-width: 0;
`;

function countFilters(filters: FilterResult): number {
	const filterTree: FilterTree | undefined = filters?.formatted;

	return filterTree ? filterTreeLength(filterTree) : 0;
}

export function useSetFilters(setReport: (value: GenericReport) => void, report: GenericReport, filters: LocalFilters, owners, filterResult?: FilterResult) {
	React.useEffect(() => {
		if (filterResult) {
			setReport({ ...report, filter: filterResult });
		}
	}, [filterResult]);

	React.useEffect(() => {
		if (filters.created_by_filter) {
			if ((filters.created_by_filter as number[]).length === owners.length) {
				setReport({ ...report, created_by_filter: undefined });
			} else {
				setReport({ ...report, created_by_filter: (filters.created_by_filter as number[]) });
			}
		}
	}, [JSON.stringify(filters.created_by_filter)]);

	React.useEffect(() => {
		if (filters.form_filter) {

			if (filters.form_filter != undefined && (report.form_filter && report.form_filter.length !== (filters.form_filter as number[]).length) || filters.form_filter && (filters.form_filter as number[]).length > 0) {
				setReport({ ...report, form_filter: (filters.form_filter as number[]) });
			} else {
				setReport({ ...report, form_filter: undefined });
			}
		}
	}, [JSON.stringify(filters.form_filter)]);

	React.useEffect(() => {
		const timerange = filters.my_filters?.timerange;

		if (timerange) {
			setReport({ ...report, my_filters: { ...report.my_filters, timerange: timerange } });
		}
	}, [JSON.stringify(filters.my_filters?.timerange)]);

	React.useEffect(() => {
		const tags = filters.my_filters?.tags;
		if (filters.my_filters) {

			if (tags && tags.length > 0) {
				setReport({ ...report, my_filters: { ...report.my_filters, tags: filters.my_filters?.tags } });
			}
			else {
				setReport({ ...report, my_filters: { ...report.my_filters, tags: undefined } });
			}
		}
	}, [JSON.stringify(filters.my_filters?.tags)]);

	React.useEffect(() => {
		const localProducts = filters.my_filters?.products?.products;
		if (localProducts && localProducts.length > 0) {
			if (filters.my_filters?.products?.all) {
				setReport({ ...report, my_filters: { ...report.my_filters, products: { all: true, products: [] } } });
			} else {
				setReport({ ...report, my_filters: { ...report.my_filters, products: { all: filters.my_filters?.products?.all ?? true, products: localProducts } } });
			}
		}
		else {
			if (filters.my_filters && filters.my_filters.products)
				setReport({ ...report, my_filters: { ...report.my_filters, products: { all: filters.my_filters.products.all, products: [] } } });
		}
	}, [JSON.stringify(filters.my_filters?.products?.products), JSON.stringify(filters.my_filters?.products?.all)]);

	React.useEffect(() => {
		const brands = filters.my_filters?.brands?.brands;

		if (brands && brands.length > 0) {
			if (filters.my_filters?.brands?.all) {
				setReport({ ...report, my_filters: { ...report.my_filters, brands: undefined } });
			} else {
				setReport({ ...report, my_filters: { ...report.my_filters, brands: { brands, all: filters.my_filters?.brands?.all ?? true } } });
			}
		}
		else {
			if (filters.my_filters && filters.my_filters.brands)
				setReport({ ...report, my_filters: { ...report.my_filters, brands: { brands: [], all: filters.my_filters.brands.all } } });
		}

	}, [JSON.stringify(filters.my_filters?.brands)]);

	React.useEffect(() => {
		if (filters.my_filters) {
			// my_filters is not in the generated type
			const myFilters = (report as GenericReport & { my_filters?: object }).my_filters;
			setReport({ ...report, my_filters: { ...myFilters, product_fields: filters.my_filters.product_fields } } as GenericReport);
		}
	}, [JSON.stringify(filters.my_filters?.product_fields)]);
}

function _VisualEditor(props: { report_id?: number, refresh: boolean, setRefresh: (value: boolean) => void }): JSX.Element {

	const [goals, setGoals] = React.useState<Goal[]>();

	const VentilationTypes: DropdownData[] = [
		{ value: 'year', label: translateToString('year') },
		{ value: 'fourMonths', label: translateToString('quadrimester') },
		{ value: 'quarter', label: translateToString('quarter') },
		{ value: 'month', label: translateToString('month') },
		{ value: 'week', label: translateToString('week') },
		{ value: 'day', label: translateToString('day') },
	];

	const VariationTypes: DropdownData[] = [
		{ value: 'N1', label: translateToString('report_editor.options.N1') },
		{ value: 'P0', label: translateToString('report_editor.options.P0') },
		{ value: undefined, label: translateToString('report_editor.options.none') }
	];

	React.useEffect(() => {
		if (props.report_id) {
			getGoals(props.report_id).then(setGoals);
		}
	}, [props.report_id]);

	const BeginPeriodTypes: DropdownData[] = [];
	const EndPeriodTypes: DropdownData[] = [];
	BeginPeriodTypes.push({ value: undefined, label: translateToString('report_editor.period.begin_default') });
	EndPeriodTypes.push({ value: undefined, label: translateToString('report_editor.period.end_default') });

	BeginPeriods.map(e => (BeginPeriodTypes.push({ value: e, label: translateToString('report_editor.period.' + e) })));
	BeginPeriodTypes.push({ value: CHOOSE_DATE, label: translateToString('report_editor.period.ChooseDate') });
	EndPeriods.map(e => (EndPeriodTypes.push({ value: e, label: translateToString('report_editor.period.' + e) })));
	EndPeriodTypes.push({ value: CHOOSE_DATE, label: translateToString('report_editor.period.ChooseDate') });


	const { report, setReport } = React.useContext(GenericEditorContext);
	const [reportType, setReportType] = React.useState<DropdownData>(report.evolution ? { value: 'evolution', label: 'evolution' } : { value: 'analyze', label: 'analyze' });
	const [reportResult, setReportResult] = React.useState<Report>();
	const [loadingState, setLoadingState] = React.useState<LoadingStateEnum>(LoadingStateEnum.LOADING);
	const { height } = useWindowDimensions();
	const { filters, jsxFilters } = React.useContext(ReportFiltersContext);
	const owners = useRecoilValue(AUsers);
	const [filterOpen, setFilterOpen] = React.useState<boolean>(false);
	const [filterParams, setFilterParams] = React.useState<FilterParameter[]>();
	const [filterResult, setFilterResult] = React.useState<FilterResult>(filters.filter as FilterResult);
	const [errorMessage, setErrorMessage] = React.useState<string | undefined>();
	const [isByProduct, setIsByProduct] = React.useState<boolean>(false);
	const { isAllowedTo } = React.useContext(PermissionContext);
	const additionalColumns = useRecoilValue(AAdditionalColumns);
	const additionalFieldColumns = useRecoilValue(AAdditionalFieldColumns);

	React.useEffect(() => {
		getCompanyFilters().then(async response => {
			const allFirstValues = await getFirstValueNotPieChart();
			const firstValues = allFirstValues.filter(fv => fv.column_type === 'additional_column');
			const newFilterParams = [...response.filters];
			if (isAllowedTo({ objectAction: 'ReadForm' })) {
				newFilterParams.push(
					additionalColumns.reduce((acc: FilterParameter, x) => {
						let type: FilterType;
						let path: string | undefined = undefined;
						if (x.type === 'ReportColumn') {
							const firstValue = firstValues.find(fv => fv.id === x.id);
							if (!firstValue || !firstValue.value) {
								type = 'nullable';
							} else if ('int' in firstValue.value || 'float' in firstValue.value) {
								type = 'numeric';
							} else if ('percentage' in firstValue.value || 'evolution' in firstValue.value) {
								type = 'percentage';
							} else if ('key' in firstValue.value || 'text' in firstValue.value) {
								type = 'string';
							} else {
								type = additionalColumnTypeToFilterTypeAndPath(x.type).type;
							}
							if (firstValue && firstValue.value) {
								path = Object.keys(firstValue.value)[0];
							}
						} else {
							const { type: type_, path: path_ } = additionalColumnTypeToFilterTypeAndPath(x.type);
							type = type_;
							path = path_;
						}
						acc.filters.push({
							id: { additional_columns: [{ id: x.id, path }] },
							name: x.name,
							type
						});
						return acc;
					}, { category: translateToString('additional_columns'), filters: [] })
				);
				newFilterParams.push(
					additionalFieldColumns.reduce((acc: FilterParameter, x) => {
						acc.filters.push({
							id: { additional_field_columns: [x.field_id] },
							name: x.field_name,
							type: fieldTypeToFilterType(x.field_type)
						});
						return acc;
					}, { category: translateToString('additional_field_columns'), filters: [] }));
			}
			if (isAllowedTo({ objectAction: 'ReadCampaign' })) {
				newFilterParams.push(await generatePromotionFilters());
			}
			setFilterParams(newFilterParams);
		});
	}, []);

	const products = useRecoilValue(AProducts);
	const formFields = useRecoilValue(AFormFields);
	const productFields = React.useMemo(() => formFields.filter(isFilterableProductField), [formFields]);
	const [, setFinalyFilteredProducts] = React.useState<string[] | undefined>();

	useSetFilters(setReport, report, filters, owners, filterResult);

	React.useEffect(() => {
		if (report.panels.some(p => p.columns.some(c => typeof c !== 'string' && 'EventColumn' in c)) && report.group_by !== 'Instance') {
			setReport({ ...report, sum: false, group_by: 'Instance' });
		}
		// external dataset reports group by client company (or product line), and the
		// company dimension entries of the picker only show under this group_by
		if (report.panels.some(p => p.columns.some(isExternalColumn)) && report.group_by !== 'Company') {
			setReport({ ...report, sum: false, group_by: 'Company' });
		}
	}, [JSON.stringify(report.panels)]);

	React.useEffect(() => {
		if (reportType.value === 'evolution') {
			const newReport = _.cloneDeep(report);
			const lines = newReport.new_group_by;
			// les rapports externes ventilent côté ClickHouse: pas de colonne
			// Index ni d'explode_by, l'évolution suffit
			const isExternal = newReport.panels.some(p => p.columns.some(isExternalColumn));
			if (!isExternal && !newReport.panels[0].columns.includes('Index')) {
				if (newReport.panels[0].columns.length === 1) {
					newReport.panels[0].columns = ['Index'];
				} else if (newReport.panels[0].columns.length > 1) {
					newReport.panels[0].columns = [
						...newReport.panels[0].columns.slice(0, lines),
						'Index',
						...newReport.panels[0].columns.slice(lines)
					];
				}
			}
			newReport.variation = undefined;
			newReport.evolution = report.evolution ?? 'month';
			newReport.sliding = false;
			if (!isExternal) {
				if (newReport.new_group_by) {
					newReport.explode_by = newReport.new_group_by;
				} else {
					newReport.new_group_by = 1;
					newReport.explode_by = 1;
				}
			}
			setReport(newReport);

		} else {
			const newReport = _.cloneDeep(report);

			newReport.panels[0].columns = newReport.panels[0].columns.filter((column) => column !== 'Index');
			newReport.explode_by = undefined;
			newReport.sliding = undefined;
			newReport.evolution = undefined;
			setReport(newReport);
		}
	}, [reportType]);

	React.useEffect(() => {
		if (report.group_by === 'Company') {
			report.panels[0].columns.forEach((column, index) => {
				if (typeof column !== 'string' && 'UserColumn' in column) {
					report.panels[0].columns[index] = { CompanyColumn: column.UserColumn };
				}
			});
		}
		else if (report.group_by === 'User') {
			report.panels[0].columns.forEach((column, index) => {
				if (typeof column !== 'string' && 'CompanyColumn' in column) {
					if (column.CompanyColumn === 'Owner') {
						column.CompanyColumn = 'Id';
					}
					report.panels[0].columns[index] = { UserColumn: column.CompanyColumn };
				}
			});
		}
		setReport(report);
	}, [report.group_by]);

	React.useEffect(() => {
		setIsByProduct(report.panels.some(panel => panel.columns.some(column => typeof column !== 'string' && 'ProductColumn' in column)));
	}, [JSON.stringify(report.panels)]);

	// Les filtres (produits, formulaires, période prise en compte…) ne
	// relancent pas la requête: on pose ses changements puis on rafraîchit
	// manuellement via le bouton. Seuls le chargement initial (les filtres
	// passent de {} à leur valeur) et props.refresh déclenchent.
	const initialLoad = React.useRef(true);
	React.useEffect(() => {
		if (JSON.stringify(filters) !== '{}') {
			if (!props.refresh && !initialLoad.current) return;
			initialLoad.current = false;
			setLoadingState(LoadingStateEnum.LOADING);
			fetchProductFieldValues(productFieldFilterIds(filters.my_filters)).then(fieldValues => {
				const productFilter = getFinalyFilteredProducts(filters, products, fieldValues);
				setFinalyFilteredProducts(productFilter);
				return getGenericAndDecodeReport({
					...report,
					range: { begin: (report.range && report.range.begin) ? report.range.begin : '90DaysAgo', end: (report.range && report.range.end) ? report.range.end : 'Today' },
					...filters,
					evolution: report.evolution,
					variation: report.variation,
					product_filter: productFilter,
					filter: (report.filter)?.formatted,
				}, undefined);
			}).then(result => {
				setReportResult(result);
				setLoadingState(LoadingStateEnum.LOADED);
				props.setRefresh(false);
			}).catch(e => {
				const decoder = new TextDecoder('utf-8');
				setLoadingState(LoadingStateEnum.ERROR);
				props.setRefresh(false);
				if (e.response?.data) {
					setErrorMessage(decoder.decode(new Uint8Array(e.response?.data)));
				} else {
					setErrorMessage(e.message);
				}
			});
		}
		else
			props.setRefresh(false);
		// l'égalité à {} en dépendance: l'initialisation des filtres déclenche
		// le premier chargement, leurs changements suivants n'appellent plus
	}, [props.refresh, JSON.stringify(filters) === '{}']);

	const alert = useAlert();
	const data_sources: DropdownData<GroupBy>[] = DataSources.map(e => ({ ...e, label: typeof e.label === 'string' ? translateToString('report_editor.data_source.' + e.label) : e.label }));
	const report_types: DropdownData<string>[] = ReportTypes.map(e => ({ ...e, label: typeof e.label === 'string' ? translateToString(e.label) : e.label }));
	const slidingOptions: DropdownData<boolean>[] = [{ label: translateToString('report_editor.sliding_dropdown.true'), value: true }, { label: translateToString('report_editor.sliding_dropdown.false'), value: false }];
	const sumOptions: DropdownData<boolean>[] = [{ label: translateToString('report_editor.sum_dropdown.true'), value: true }, { label: translateToString('report_editor.sum_dropdown.false'), value: false }];

	return <>
		{filterParams && <AdvancedFilters
			permission='ReadCompany'
			isOpen={filterOpen}
			setOpen={setFilterOpen}
			isOpenSummary={true}
			filterList={filterParams}
			filterValues={filterResult?.values}
			onChange={(value) => setFilterResult(value)}
		/>}
		<FlexDiv gap='10px' height='100%'>
			<Wrapper>
				<InnerWrapper>
					<TitleAndChild
						margin='0 30 0 0'
						addDiv
						defaultOpen={true}
						localCollapseStyle={{ justifyContent: 'left' }}
						title={translateToString('report_editor.report_options')}>
						<OptionsModal>
							<ReportOption>
								<Translate id='report_editor.period.title' /> début
								<FlexDiv flow='column' gap='5px' align='flex-end'>
									<Dropdown
										name='range'
										dropdownStyle={{ width: '200px', optionWidth: '200px' }}
										datalist={BeginPeriodTypes}
										selectedValue={BeginPeriodTypes.find(a => {
											if (isCustomRangeDate(report.range?.begin)) {
												return a.value === CHOOSE_DATE;
											} else if (report.range?.begin && report.range?.begin !== '90DaysAgo') {
												return a.value === report.range?.begin;
											} else {
												return a.value === undefined;
											}
										})
										}
										onChange={value => {
											const begin = value.value === CHOOSE_DATE ? moment().format('YYYY-MM-DD[T]00:00:00') : value.value ?? '90DaysAgo';
											setReport({ ...report, range: { begin, end: report.range?.end ?? 'Today' } });
										}}
									/>
									{isCustomRangeDate(report.range?.begin) && <RangeDateInput
										type='date'
										name='range_begin_date'
										value={moment(report.range?.begin).format('YYYY-MM-DD')}
										onChange={e => e.target.value && setReport({
											...report,
											range: { begin: `${e.target.value}T00:00:00`, end: report.range?.end ?? 'Today' },
										})}
									/>}
								</FlexDiv>
							</ReportOption>
							<ReportOption>
								<Translate id='report_editor.period.title' /> fin
								<FlexDiv flow='column' gap='5px' align='flex-end'>
									<Dropdown
										name='range'
										dropdownStyle={{ width: '200px', optionWidth: '200px' }}
										datalist={EndPeriodTypes}
										selectedValue={EndPeriodTypes.find(a => {
											if (isCustomRangeDate(report.range?.end)) {
												return a.value === CHOOSE_DATE;
											} else if (report.range?.begin && report.range?.end !== 'Today') {
												return a.value === report.range?.end;
											} else {
												return a.value === undefined;
											}
										})
										}
										onChange={value => {
											const end = value.value === CHOOSE_DATE ? moment().format('YYYY-MM-DD[T]23:59:59') : value.value ?? 'Today';
											setReport({ ...report, range: { end, begin: report.range?.begin ?? '90DaysAgo' } });
										}}
									/>
									{isCustomRangeDate(report.range?.end) && <RangeDateInput
										type='date'
										name='range_end_date'
										value={moment(report.range?.end).format('YYYY-MM-DD')}
										onChange={e => e.target.value && setReport({
											...report,
											range: { end: `${e.target.value}T23:59:59`, begin: report.range?.begin ?? '90DaysAgo' },
										})}
									/>}
								</FlexDiv>
							</ReportOption>
							<ReportOption>
								<Translate id='report_editor.report_type' />
								<Dropdown datalist={report_types} name='type' selectedValue={report_types.find(a => a.value === reportType.value)} onChange={
									value => {
										if (goals && goals.length > 0 && reportType.value !== value.value) {
											alert({
												title: translateToString('warning'),
												content: 'Si vous changez le type de rapport, vous perdrez vos objectifs. Voulez-vous continuer ?',
												mode: 'delete'
											}).then(res => {
												if (res.res === AlertRes.Ok) {
													setReportType(value);
												}
											}
											);

										} else {
											setReportType(value);
										}
									}
								} />
							</ReportOption>
							<ReportOption>
								<Translate id='report_editor.data_source.title' />
								<Dropdown datalist={data_sources}
									dropdownStyle={{ width: '200px', optionWidth: '200px' }}
									name='source'
									selectedValue={(data_sources.find(a => a.value === report.group_by))}
									onChange={value => {
										if (value.value === 'Instance')
											setReport({ ...report, group_by: value.value, sum: false });
										else
											setReport({ ...report, group_by: value.value });
									}} />
							</ReportOption>
							<ReportOption>
								<Translate id='report_editor.hidden_filters' />
								<Switch value={report.hidden_filters} onChange={v => setReport({ ...report, hidden_filters: v })} />
							</ReportOption>
							{reportType && reportType.value === 'evolution' && <ReportOption>
								<Translate id='report_editor.default_ventilation' />
								<Dropdown datalist={VentilationTypes} name='evolution' selectedValue={VentilationTypes.find(a => a.value === report.evolution) ?? { value: 'month', label: 'Mois' }}
									onChange={value => {
										if (goals && goals.length > 0 && report.evolution !== value.value) {
											alert({
												title: translateToString('warning'),
												content: 'Des objectifs sont assignés sur cette ventilation, vous allez potentiellement devoir les réajuster. Voulez-vous continuer ?',
												mode: 'delete'
											}).then(res => {
												if (res.res === AlertRes.Ok) {
													setReport(setEvolutionBy(report, value.value));
												}
											}
											);
										}
										else {
											setReport(setEvolutionBy(report, value.value));
										}
									}} />
							</ReportOption>}
							{report.group_by !== 'Instance' && <ReportOption>
								<Translate id='report_editor.sum_dropdown.title' />
								<Dropdown datalist={sumOptions}
									dropdownStyle={{ width: '200px', optionWidth: '200px' }}
									name='source'
									selectedValue={report.sum !== undefined ? (sumOptions.find(a => a.value === report.sum)) : (sumOptions.find(a => a.value === false))}
									onChange={value => {
										setReport({ ...report, sum: value.value });
									}} />
							</ReportOption>}
							{reportType && reportType.value === 'evolution' && <ReportOption>
								<Translate id='report_editor.sliding_dropdown.title' />
								<Dropdown datalist={slidingOptions}
									dropdownStyle={{ width: '200px', optionWidth: '200px' }}
									name='source'
									selectedValue={report.sliding !== undefined ? (slidingOptions.find(a => a.value === report.sliding)) : (slidingOptions.find(a => a.value === true))}
									onChange={value => {
										setReport({ ...report, sliding: value.value });
									}} />
							</ReportOption>}
							{reportType && reportType.value === 'analyze' && <ReportOption>
								<Translate id='report_editor.options.show_variation' />
								<Dropdown datalist={VariationTypes}
									dropdownStyle={{ width: '200px', optionWidth: '200px' }}
									name='variation'
									selectedValue={report.variation ? (VariationTypes.find(a => a.value === report.variation)) : { value: undefined, label: translateToString('report_editor.options.none') }}
									onChange={value => {
										if (value.value) {
											alert({
												title: translateToString('warning'),
												content: 'Si vous activez la variation, certaines valeurs peuvent être transformés en lignes, notamment les champs de société, d\'instances, de produits, ainsi que les champs additionnels.',
											}).then(res => {
												if (res.res === AlertRes.Ok) {
													setReport(setVariation(report, value.value));
												}
											});
										}
										else {
											setReport(setVariation(report, value.value));
										}
									}}
								/>
							</ReportOption>}
							{isByProduct && <ReportOption>
								<Translate id='report_editor.force_products' />
								<Switch value={report.force_all_products} onChange={v => setReport({ ...report, force_all_products: v })} />
							</ReportOption>}
						</OptionsModal>
					</TitleAndChild>
					<TitleAndChild
						title={translateToString('report_editor.report_lines')}
						margin='0 30 0 0'
						addDiv
						defaultOpen={true}
						localCollapseStyle={{ justifyContent: 'left' }}
					>
						<>
							<EditorWrapper>
								<RecursiveReportEditor only_lines report_id={props.report_id} />
							</EditorWrapper>
						</>
					</TitleAndChild>
					{!report.variation && <TitleAndChild
						margin='0 30 0 0'
						addDiv
						defaultOpen={true}
						localCollapseStyle={{ justifyContent: 'left' }}
						title={translateToString('report_editor.report_columns')}>
						<EditorWrapper>
							<RecursiveReportEditor only_columns />
						</EditorWrapper>
					</TitleAndChild>}
					<TitleAndChild
						margin='0 30 0 0'
						addDiv
						defaultOpen={true}
						localCollapseStyle={{ justifyContent: 'left' }}
						title={translateToString('report_editor.report_values')}>
						<EditorWrapper>
							<RecursiveReportEditor />
						</EditorWrapper>
					</TitleAndChild>
					<TitleAndChild
						margin='0 30 0 0'
						addDiv
						defaultOpen={true}
						localCollapseStyle={{ justifyContent: 'left' }}
						title={translateToString('report_editor.filters')}>
						<OptionsModal gap='16px'>
							<FlexDiv flow='column' align='stretch' gap='8px'>
								<Translate id='report_editor.filter_categories.form_instances' />
								{jsxFilters && jsxFilters[0] && jsxFilters[0][1].child}
							</FlexDiv>
							<FlexDiv flow='column' align='stretch' gap='8px'>
								<Translate id='report_editor.filter_categories.form_models' />
								{jsxFilters && jsxFilters[1] && jsxFilters[1][1].child}
							</FlexDiv>
							<FlexDiv flow='column' align='stretch' gap='8px'>
								<Translate id='report_editor.filter_categories.form_creator' />
								{jsxFilters && jsxFilters[1] && jsxFilters[1][0].child}
							</FlexDiv>
							<FlexDiv flow='column' align='stretch'>
								<div style={{ padding: '0 0 8px 0' }}><Translate id='report_editor.filter_categories.product_categories' /></div>
								{jsxFilters && jsxFilters[2] && jsxFilters[2][0].child}
							</FlexDiv>
							<FlexDiv flow='column' align='stretch' gap='8px'>
								<Translate id='report_editor.filter_categories.brands' />
								{jsxFilters && jsxFilters[2] && jsxFilters[2][1].child}
							</FlexDiv>
							<FlexDiv flow='column' align='stretch' gap='8px'>
								<Translate id='report_editor.filter_categories.product_tags' />
								{jsxFilters && jsxFilters[2] && jsxFilters[2][2].child}
							</FlexDiv>
							{productFields.map(field => <FlexDiv key={field.id} flow='column' align='stretch' gap='8px'>
								{field.name}
								<ProductFieldFilter field={field} fullScreen />
							</FlexDiv>)}
							<div>
								<DefaultButton margin='0' onClick={() => setFilterOpen(true)}><Translate id="global.filter.company_filters" /> ({countFilters(filterResult)})</DefaultButton>
							</div>
						</OptionsModal>
					</TitleAndChild>
				</InnerWrapper>
			</Wrapper>
			{loadingState === LoadingStateEnum.ERROR ?
				<NoData message={reportErrorMessage(errorMessage)} isError={true} height='100%' borderRadius='10px'></NoData> :
				<ReportWrapper>
					<ComponentLoader loadingState={loadingState} allScreen />
					{reportResult && <ReportInterpretor
						report={reportResult}
						columnColorsFrom={report}
						setToolBarState={() => ({})}
						height={height - TOOLBARS_HEIGHT - 148}
						onCompanyClick={() => ({})}
						onFormInstanceClick={() => ({})}
					/>}
				</ReportWrapper>}
		</FlexDiv>
	</>;
}

const UnderlineBefore = styled.div`
	position: relative;
	padding: 4px 0px;
	::before {
		position: absolute;
		left: 15px;
		bottom: 0;
		width: 75%;
		height: 1px;
		content: '';
	}
	&:hover::before {
		background-color: ${PrimaryColor};
	}
`;

const TitleInput = styled.input<{ width?: number }>`
	text-overflow: ellipsis;
	white-space: nowrap;
	overflow: hidden;
	padding-left: 15px;
	border: none;
	width: ${p => p.width ? p.width + p.width * 0.1 + 'ch' : '100%'};
	max-width: 80vw;
	min-width: 100px;
`;

const ContentWrapper = styled.div`
	width: 100%;
	height: calc(100vh - 130px);
	font-size: 13px;
	border-radius: 10px;
	border-radius: 5px;
	box-shadow: 0px 0px 15px 4px color-mix(in oklab, ${SidelyBlack} 1%, transparent);
`;

export default function GenericEditor(props: ComponentProps): JSX.Element {
	const history = useHistory();
	const [localFnReport, setLocalFnReport] = React.useState<LocalStorageReport | undefined>(getLocalStorage('current-report'));
	const [reportText, setReportText] = React.useState<string | undefined>(JSON.stringify(localFnReport?.report?.report, null, 2) ?? JSON.stringify({}));
	const [currentView, setCurrentView] = React.useState<number>(Views.Visual);
	const [reportTitle, setReportTitle] = React.useState<string | undefined>(localFnReport?.report?.name);
	const [refresh, setRefresh] = React.useState<boolean>(false);

	React.useEffect(() => {
		props.setToolBarState({
			title: translateToString('report_editor.title'),
			bottomLeftToolbarComponent: <FlexDiv>
				<UnderlineBefore>
					<TitleInput type='text' value={reportTitle} onChange={(e) => setReportTitle(e.target.value)} width={reportTitle?.length}></TitleInput>
				</UnderlineBefore>
				<RefreshButton isLoading={refresh} onFilter={() => setRefresh(true)} label={translateToString('report_editor.refresh')} />
			</FlexDiv>,
			bottomRightToolbarComponent:
				<FlexDiv flexWrap='nowrap'>
					<DefaultButton onClick={() => {
						if (localFnReport && localFnReport.report && reportText) {
							putReport(localFnReport.report.id, { name: reportTitle?.trim() ?? localFnReport.report.name, value: JSON.parse(reportText) }).then(
								() => {
									if (localFnReport && localFnReport.report) {
										localStorage.setItem('report_id_to_view', localFnReport.report.id.toString());
										getReportById(localFnReport.report.id).then(setLocalFnReport);
										history.push('/reports#generic', { from: 'editor' });
									}
									else {
										history.push('/reports#list', { from: 'editor' });
									}
								}
							);
						}
					}} height="5%" disabled={(reportText === JSON.stringify(localFnReport?.report?.report, null, 2)) && localFnReport?.report?.name === reportTitle}><Translate id='save' />
					</DefaultButton>
					<CrossClose src={close} size='28px' onClick={() => history.push('/reports#generic', { from: 'editor' })} />
				</FlexDiv>,
		});
	}, [reportText, reportTitle]);


	return <>
		<ContentWrapper>
			<FlexDiv padding='0px 0px 10px 10px'>
				<Tab selected={currentView === Views.Code} onClick={() => setCurrentView(Views.Code)} ><Translate id='report_editor.code_editor' /></Tab>
				<Tab selected={currentView === Views.Visual} onClick={() => setCurrentView(Views.Visual)}><Translate id='report_editor.visual_editor' /></Tab>
			</FlexDiv>
			{currentView === Views.Visual &&
				<VisualEditor report_id={localFnReport?.report?.id} report={reportText} setReport={setReportText} refresh={refresh} setRefresh={setRefresh} />
			}
			{currentView === Views.Code && <Editor
				height="85%"
				defaultLanguage='json'
				defaultValue={reportText}
				onChange={setReportText}
			/>}
		</ContentWrapper>
	</>;
}
